import React, { useEffect, useState } from 'react';
import './DueInvoice.css';
import { jsPDF } from "jspdf";
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import one from '../../assets/1.jpg'; // Colkan header image
import two from '../../assets/2.jpg'; // Haman header image
import three from '../../assets/3.jpg'; // TerraWalkers header image

const DueInvoice = () => {
    const { cusId } = useParams();
    const location = useLocation();
    const [customer, setCustomer] = useState(null);
    const [invoiceData, setInvoiceData] = useState([]);
    const [transactionData, setTransactionData] = useState([]); // Add transaction data for status check
    const [invoiceNumbers, setInvoiceNumbers] = useState({}); // To map invoice IDs to numbers
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(10);

    const [Colkan, setColkan] = useState(false);
    const [Haman, setHaman] = useState(false);
    const [TerraWalkers, setTerraWalkers] = useState(false);

    // Get dateRange from location state, default to empty if not provided
    const dateRange = location.state?.dateRange || { startDate: '', endDate: '' };

    // Function to check if a date is within the selected range
    const isDateInRange = (dateToCheck) => {
        if (!dateRange.startDate && !dateRange.endDate) return true;

        const date = new Date(dateToCheck);
        const start = dateRange.startDate ? new Date(dateRange.startDate) : null;
        const end = dateRange.endDate ? new Date(dateRange.endDate) : null;

        if (start && end) {
            return date >= start && date <= end;
        } else if (start) {
            return date >= start;
        } else if (end) {
            return date <= end;
        }
        return true;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const customerResponse = await axios.get(`${config.BASE_URL}/customer/${cusId}`);
                setCustomer(customerResponse.data);

                switch (customerResponse.data.cusStore) {
                    case 'Colkan':
                        setColkan(true);
                        break;
                    case 'Haman':
                        setHaman(true);
                        break;
                    case 'TerraWalkers':
                        setTerraWalkers(true);
                        break;
                    default:
                        break;
                }

                // Fetch invoice payment details
                const invoiceResponse = await axios.get(`${config.BASE_URL}/duecustomer/invoice/${cusId}`);
                setInvoiceData(invoiceResponse.data);

                // Fetch transaction data to determine paid status
                const transactionResponse = await axios.get(`${config.BASE_URL}/transactions/customer/${cusId}`);
                setTransactionData(transactionResponse.data);

                // Fetch invoice numbers
                const fetchInvoiceNumbers = async () => {
                    const invoiceNumbersMap = {};
                    for (const transaction of transactionResponse.data) {
                        try {
                            const invoiceResponse = await axios.get(`${config.BASE_URL}/invoice/${transaction.invoice_invoiceId}`);
                            invoiceNumbersMap[transaction.invoice_invoiceId] = invoiceResponse.data.invoiceNo;
                        } catch (error) {
                            console.error('Error fetching invoice details:', error);
                            invoiceNumbersMap[transaction.invoice_invoiceId] = 'N/A';
                        }
                    }
                    setInvoiceNumbers(invoiceNumbersMap);
                };

                await fetchInvoiceNumbers();
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Failed to fetch data');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [cusId]);

    // Filter paid invoices within the date range
    const paidInvoices = invoiceData
        .filter(payment => {
            const transaction = transactionData.find(t => t.invoice_invoiceId === payment.invoiceId);
            return transaction && transaction.due === 0 && isDateInRange(payment.dueDate);
        });

    // Pagination logic
    const totalPages = Math.ceil(paidInvoices.length / rowsPerPage);
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = paidInvoices.slice(indexOfFirstRow, indexOfLastRow);

    const getPageNumbers = () => {
        let pages = [];
        pages.push(1);
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            if (i > 1 && i < totalPages) pages.push(i);
        }
        if (totalPages > 1) pages.push(totalPages);
        return [...new Set(pages)].sort((a, b) => a - b);
    };

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
    const handlePrevPage = () => currentPage > 1 && setCurrentPage(prev => prev - 1);
    const handleNextPage = () => currentPage < totalPages && setCurrentPage(prev => prev + 1);

    const handlePrint = () => {
        const printContent = document.getElementById('invoice-card');
        if (printContent) {
            const doc = new jsPDF();
            doc.html(printContent, {
                callback: function (doc) {
                    doc.autoPrint();
                    window.open(doc.output('bloburl'), '_blank');
                    doc.save('Due_Invoice.pdf');
                },
                x: 10,
                y: 10,
                width: 190,
                windowWidth: 800,
            });
        } else {
            console.error('Invoice card not found!');
        }
    };

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <div className="scrolling-container">
            <h4>DUE INVOICE</h4>
            <div className="invoice-page">
                <div className="invoice">
                    <div id="invoice-card">
                        {/* Company Header */}
                        {Colkan && <section className="invoice-header"><img src={one} alt="Colkan Holdings" className="header-img" /></section>}
                        {Haman && <section className="invoice-header"><img src={two} alt="Haman" className="header-img" /></section>}
                        {TerraWalkers && <section className="invoice-header"><img src={three} alt="Terra Walkers" className="header-img" /></section>}

                        {/* Customer Details */}
                        <section className="billing-details">
                            <div className="invoice-info">
                                <label className="mb-3"><b>Customer Details</b></label>
                                <p className="details-paragraph">
                                    {customer?.cusName && `Name: ${customer.cusName}. `} <br />
                                    {customer?.cusJob && `Job Title: ${customer.cusJob}. `} <br />
                                    {customer?.cusAddress && `Address: ${customer.cusAddress}.`} <br />
                                </p>
                            </div>
                            <div className="invoice-info">
                                <div className="details mb-2">
                                    <label>Date</label>
                                    <input type="text" className="date" value={new Date().toLocaleDateString()} readOnly />
                                </div>
                            </div>
                        </section>

                        {/* Payment Mode and Bank Details */}
                        <section>
                            <p>
                                <b>Payment Mode:</b> Cash or cheque. All cheques are to be drawn in favour of {Colkan ? "Colkan Holdings (Pvt) Ltd" : Haman ? "Haman" : TerraWalkers ? "Terra Walkers" : "[Company Name]"} and crossed account payee only.
                            </p>
                            <p>
                                <b>Bank:</b> {Colkan ? "HNB | Account Number: 250010032342 | Account Name: Colkan Holdings (Pvt) LTD | Branch Name: Colkan" :
                                    Haman ? "BOC | Account Number: 93829087 | Account Name: Haman | Branch Name: Wellewathe" :
                                    TerraWalkers ? "Sampath Bank | Account Number: 0117 1000 1407 | Account Name: TerraWalkers | Branch Name: Kirulapona" :
                                    "[Bank Details]"}
                            </p>
                        </section>

                        {/* Invoice Table */}
                        <table className="invoice-table mb-2">
                            <thead>
                                <tr>
                                    <th>Invoice No</th>
                                    <th>Cheque Details</th>
                                    <th>Cheque Given Date</th>
                                    <th>Dated Cheque</th>
                                    <th>Paid</th>
                                    <th>Paid Yes Or No</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentRows.length === 0 ? (
                                    <tr><td colSpan={6}>No paid invoices found within the selected date range.</td></tr>
                                ) : (
                                    currentRows.map((invoice, index) => (
                                        <tr key={index}>
                                            <td>{invoiceNumbers[invoice.invoiceId] || invoice.invoiceId}</td>
                                            <td>{invoice.chequeDetail || 'N/A'}</td>
                                            <td>{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}</td>
                                            <td>{invoice.datedCheque ? new Date(invoice.datedCheque).toLocaleDateString() : 'N/A'}</td>
                                            <td>{invoice.paidAmount}</td>
                                            <td><span style={{ color: 'green', fontWeight: 'bold' }}>Yes</span></td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="pagination-controls d-flex justify-content-center align-items-center gap-2 mb-3">
                                <button onClick={handlePrevPage} disabled={currentPage === 1} className="btn btn-sm btn-outline-primary">&lt;</button>
                                {getPageNumbers().map((pageNum, index, array) => (
                                    <React.Fragment key={pageNum}>
                                        {index > 0 && array[index - 1] !== pageNum - 1 && <span>...</span>}
                                        <button
                                            onClick={() => handlePageChange(pageNum)}
                                            className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-outline-primary'}`}
                                        >
                                            {pageNum}
                                        </button>
                                    </React.Fragment>
                                ))}
                                <button onClick={handleNextPage} disabled={currentPage === totalPages} className="btn btn-sm btn-outline-primary">&gt;</button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="options">
                    <button onClick={handlePrint} className="btn btn-success">Print</button>
                </div>
            </div>
        </div>
    );
};

export default DueInvoice;